import styles from "./TestSendAutomatedEmail.module.scss";
import React, { useEffect, useMemo, useRef } from "react";
import {
  useGetEmailTemplateByIdQuery,
  useGetEmailTemplateSubjectsQuery,
} from "../../../api/queries";
import Loader from "../../../components/Loader/Loader";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { openErrorMessage, openSuccessMessage } from "../../../store/message/messageSlice";
import { useTranslation } from "react-i18next";
import closeIcon from "../../../assets/images/icons/close-grey.svg";
import { Form, FormInstance } from "antd";
import { EmailTemplateLanguageType, EmailTemplateSubjectEnum } from "../../../models/EmailTemplate";
import FormItemSelect from "../../../components/ui/FormItemSelect/FormItemSelect";
import FormItemInput from "../../../components/ui/FormItemInput/FormItemInput";
import MainTitle from "../../../components/ui/MainTitle/MainTitle";
import { useEmailTemplateMutations } from "../../../api/mutations/useEmailTemplateMutations";
import { handleError } from "../../../helper/errorHandler";
import CustomButton from "../../../components/ui/CustomButton/CustomButton";

interface ITestSendAutomatedEmailProps {
  id: string;
  closePopup: () => void;
}

const TestSendAutomatedEmail: React.FC<ITestSendAutomatedEmailProps> = ({ id, closePopup }) => {
  const formRef = useRef<FormInstance>(null);
  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data, isLoading, error } = useGetEmailTemplateByIdQuery(id);
  const { data: subjects = [] } = useGetEmailTemplateSubjectsQuery();
  const { testSendEmailTemplate } = useEmailTemplateMutations();

  const languageItems = useMemo(() => {
    if (!data) return [];
    return data.emailTemplateVersions.map((item) => {
      return {
        id: item.language,
        label: t(item.language),
      };
    });
  }, [data]);

  const subjectSelectItems = useMemo(() => {
    return subjects.map((el) => {
      const label = i18n.language === "en" ? el.labelEn : el.labelEl;
      return {
        id: el.id,
        label,
        disabled: el.alreadyInUse,
      };
    });
  }, [i18n.language, subjects]);

  useEffect(() => {
    if (data && subjects.length && formRef.current) {
      formRef.current.setFieldsValue({
        subject: data.subject,
      });
    }
  }, [subjects, data, formRef]);

  const onFinish = async (values: {
    subject: EmailTemplateSubjectEnum;
    language: EmailTemplateLanguageType;
    to: string;
  }) => {
    const { subject, to, language } = values;
    console.log(values);
    testSendEmailTemplate(
      {
        subject,
        to,
        language,
      },
      {
        onSuccess: (res) => {
          console.log(res);
          if (res) {
            dispatch(openSuccessMessage(t("EmailSentSuccessfully")));
          } else {
            dispatch(openErrorMessage(t("SOMETHING_WENT_WRONG")));
          }
          closePopup();
        },
        onError: (e) => handleError(e, dispatch, t),
      }
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    dispatch(openErrorMessage(t("EmailTemplateNotFound")));
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.popup}>
        <button onClick={closePopup} className={styles.closeBtn}>
          <img src={closeIcon} alt="close" />
        </button>
        <MainTitle text={t("TestSend")} titleSize={26} />
        <Form
          className={"form"}
          ref={formRef}
          name="create-email-template"
          onFinish={onFinish}
          autoComplete="off"
          layout={"vertical"}
        >
          <FormItemSelect
            name={"subject"}
            label={t("EmailSubject")}
            rules={{ required: true }}
            items={subjectSelectItems}
            disabled={true}
          />

          <FormItemSelect
            name={"language"}
            label={t("Language")}
            rules={{ required: true }}
            items={languageItems}
          />

          <FormItemInput name={"to"} label={t("EmailAddress")} rules={{ required: true }} />

          <CustomButton text={t("Send")} type={"submit"} typeStyle={"fill"} />
        </Form>
      </div>
    </div>
  );
};

export default TestSendAutomatedEmail;
